// =============================================================================
// io/sync/saveGoal.ts — create, update and delete a period target.
// -----------------------------------------------------------------------------
// ── 🚨 SEVEN COLUMNS, AND THE THREE THAT ARE ABSENT ARE THE DESIGN ──
//
// `goals` has eleven columns. This writer names seven, and the four it leaves
// out are left out on purpose:
//
//   `id`         — the key. Minted once, on insert, and never rewritten.
//   `extras`     — not read by this client and not rendered by this form, so
//                  naming it in an UPDATE would blank whatever is in there
//                  (D62, and the journal contract paid for that lesson once).
//   `created_at` — has a default; sending it on an update would rewrite when the
//                  goal was made every time its name is edited.
//   `updated_at` — a trigger owns it.
//
// ⚠ `type` IS written, and it is the only column written without being read.
// The argument is in `domain/goalForm.ts` note 1: it is a pure function of
// `template`, the old client recomputes it on every save too, and omitting it
// would leave `'dollar'` sitting beside `template: 'tag-cap'`.
//
// ── 🚨 THE SAVE REPORTS "MATCHED NO ROWS" RATHER THAN SUCCEEDING QUIETLY ──
//
// A PostgREST update against a row that RLS hides succeeds with an empty result.
// Without the check below, deleting a goal in another tab and then saving it
// here would report success and write nothing.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Goal } from '../../domain/goals';
import {
  goalStorageName, goalType, mergeGoalParams, ownedParams, type GoalDraft,
} from '../../domain/goalForm';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface SaveGoalResult {
  id: string | null;
  error: string | null;
}

/**
 * The row.
 *
 * @param prior the goal as loaded, or `null` for a new one. Only used to decide
 *   whether the stored params blob is spread first — see `mergeGoalParams`.
 * @param choices the option lists the form was rendering, so a select's value
 *   is resolved against what was actually on screen rather than against a
 *   stored key that may no longer exist.
 */
export function goalToRow(
  draft: GoalDraft,
  prior: Goal | null,
  userId: string,
  choices: { booleanKeys?: readonly string[]; tags?: readonly string[] } = {},
): Record<string, unknown> {
  const template = draft.template;
  // ⚠ `scopes` is deliberately not passed. The Trades/Days pair is fixed by the
  // template rather than by anything on the page, so the default in
  // `ownedParams` is the whole list — there is nothing for a caller to vary.
  const owned = ownedParams(template, draft.params, choices);

  return {
    user_id: userId,
    name: goalStorageName(draft.name),
    type: goalType(template),
    template,
    period: draft.period,
    params: mergeGoalParams(template, owned, prior),
    // ⚠ A COPY. Handing the draft's own array to the client leaves the caller
    // able to mutate what was sent, and the account picker edits in place.
    account_ids: [...draft.accountIds],
  };
}

export async function insertGoal(
  sb: SupabaseClient, id: string, draft: GoalDraft, userId: string,
  choices: { booleanKeys?: readonly string[]; tags?: readonly string[] } = {},
): Promise<SaveGoalResult> {
  if (!id) return { id: null, error: 'Refusing to insert a goal with no id.' };
  const { error } = await sb
    .from('goals').insert({ id, ...goalToRow(draft, null, userId, choices) });
  if (error) {
    return { id: null, error: reportFailureAs('add goal', error, `Save failed: ${error.message}`) };
  }
  return { id, error: null };
}

export async function updateGoal(
  sb: SupabaseClient, id: string, draft: GoalDraft, prior: Goal, userId: string,
  choices: { booleanKeys?: readonly string[]; tags?: readonly string[] } = {},
): Promise<SaveGoalResult> {
  const { data, error } = await sb
    .from('goals').update(goalToRow(draft, prior, userId, choices)).eq('id', id).select('id');

  if (error) {
    return { id: null, error: reportFailureAs('save goal', error, `Save failed: ${error.message}`) };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return {
      id: null,
      error: 'That save matched no rows, so nothing changed. The goal may have been '
        + 'deleted from another tab.',
    };
  }
  return { id, error: null };
}

/**
 * Delete a goal.
 *
 * ⚠ Nothing references `goals.id` — no trade, no journal entry — so this loses
 * only the target itself. That is why the confirmation says what it does rather
 * than warning about knock-on damage there is none of.
 */
export async function deleteGoal(
  sb: SupabaseClient, userId: string, id: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('goals').delete().eq('user_id', userId).eq('id', id).select('id');
  if (error) {
    return { error: reportFailureAs('delete goal', error, `Delete failed: ${error.message}`) };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { error: 'That delete matched no rows — the goal may already be gone.' };
  }
  return { error: null };
}
